/**
 * The presence heartbeat (packages/shared/src/presence.ts has the rules, POST /api/presence the storage).
 *
 * While a tab is open it tells the server, every minute when visible and every five when hidden, the page's path,
 * whether it is visible, and when the person last interacted with it. Nothing else leaves the tab: no content, no
 * query strings, no pointer positions - the listeners only note the time.
 *
 * "Interacted" is a key, a click or touch, a scroll or wheel, the pointer moving, the window taking focus, or a
 * video or audio element playing (the clip editor's playback). The listeners are passive and cost one comparison
 * per event: the time is noted at most once a second. A first interaction after a quiet spell beats at once, so
 * the Activity page turns someone active within seconds, not a minute.
 */
import { PRESENCE } from "@scandi/shared";
import { tabId } from "@/lib/surface";

const INPUT_EVENTS = [
  "pointerdown",
  "keydown",
  "wheel",
  "scroll",
  "touchstart",
  "pointermove",
  "input",
] as const;
/** The closest two beats may be (the server drops closer ones anyway). */
const MIN_GAP_MS = 15_000;

let lastInput: number | null = null;
let lastSent = 0;
let timer: number | null = null;
let running = false;

function mediaPlaying(): boolean {
  for (const el of document.querySelectorAll<HTMLMediaElement>(
    "video, audio",
  )) {
    if (!el.paused && !el.ended && el.readyState > 2) return true;
  }
  return false;
}

function send(closing = false): void {
  const now = Date.now();
  if (!closing && now - lastSent < MIN_GAP_MS) return;
  if (mediaPlaying()) lastInput = now;
  lastSent = now;
  const body = JSON.stringify({
    tab: tabId(),
    page: window.location.pathname.slice(0, 200),
    visible: document.visibilityState === "visible",
    lastInputAt: lastInput === null ? null : new Date(lastInput).toISOString(),
    ...(closing ? { closing: true } : {}),
  });
  // keepalive lets the closing beat outlive the page; credentials ride along as for any same-origin request.
  void fetch("/api/presence", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body,
    keepalive: true,
    credentials: "same-origin",
  }).catch(() => undefined);
}

function schedule(): void {
  if (timer !== null) window.clearTimeout(timer);
  const every =
    document.visibilityState === "visible"
      ? PRESENCE.BEAT_VISIBLE_MS
      : PRESENCE.BEAT_HIDDEN_MS;
  timer = window.setTimeout(() => {
    send();
    schedule();
  }, every);
}

function onInput(): void {
  const now = Date.now();
  if (lastInput !== null && now - lastInput < 1_000) return;
  const quiet =
    lastInput === null || now - lastInput > PRESENCE.ACTIVE_WINDOW_MS;
  lastInput = now;
  // Back from a quiet spell: say so now rather than at the next minute.
  if (quiet) send();
}

function onVisibility(): void {
  if (document.visibilityState === "visible") onInput();
  lastSent = 0;
  send();
  schedule();
}

function onPageHide(): void {
  send(true);
}

function onPageShow(event: PageTransitionEvent): void {
  // Restored from the back/forward cache: the tab is open again.
  if (event.persisted) onVisibility();
}

/** Start the heartbeat for this tab; answers the stop. Safe to call twice. */
export function startPresence(): () => void {
  if (running || typeof window === "undefined") return () => undefined;
  running = true;
  for (const name of INPUT_EVENTS)
    document.addEventListener(name, onInput, { capture: true, passive: true });
  document.addEventListener("play", onInput, { capture: true, passive: true });
  window.addEventListener("focus", onInput);
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("pageshow", onPageShow);
  if (document.visibilityState === "visible" && document.hasFocus())
    lastInput = Date.now();
  send();
  schedule();
  return () => {
    running = false;
    for (const name of INPUT_EVENTS)
      document.removeEventListener(name, onInput, { capture: true });
    document.removeEventListener("play", onInput, { capture: true });
    window.removeEventListener("focus", onInput);
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("pagehide", onPageHide);
    window.removeEventListener("pageshow", onPageShow);
    if (timer !== null) window.clearTimeout(timer);
    timer = null;
  };
}
